import { Component, OnInit } from '@angular/core'
import { Meta, Title } from '@angular/platform-browser'
import { Router } from '@angular/router'
import { GtmApiService, GtmSessionExpired } from '../../services/gtm-api/gtm-api.service'
import {
	BUILT_IN_TRIGGERS,
	expectedEventName,
	GtmReport,
	GtmTag,
	GtmTrigger,
	triggersForClickClass,
	triggersForEvent,
} from '../../services/gtm-api/gtm-report'
import { GtmReportAuthService } from '../../services/gtm-report-auth/gtm-report-auth.service'

interface FormItem {
	name: string
	where: string
	// Evento enviado ao GTM no envio do formulário (sem evento = formulário não medido)
	event?: string
}

interface ClickItem {
	name: string
	where: string
	// Classes CSS que marcam o elemento para os acionadores de clique do GTM
	classes: string[]
}

interface TrackedRow {
	name: string
	where: string
	tools: string[]
}

interface TagRow {
	tag: GtmTag
	name: string
	when: string[]
}

interface PageVisits {
	page: string
	tools: string[]
}

// Mapeamento feito no código do site: formulários e botões que existem hoje
const FORMS: FormItem[] = [
	{ name: 'Seja um revendedor', where: 'Página Seja revendedor', event: 'sendBeReseller' },
	{ name: 'Para empresas', where: 'Página Para empresas', event: 'sendB2B' },
	{ name: 'Contato', where: 'Página Atendimento', event: 'sendContactUs' },
	{ name: 'Newsletter', where: 'Rodapé de todas as páginas' },
	{ name: 'Pedido de orçamento', where: 'Página Onde encontrar' },
	{ name: 'Pedido de orçamento', where: 'Página Onde encontrar revenda Everest' },
	{ name: 'Máquina de gelo', where: 'Página Máquinas de gelo' },
	{ name: 'Exportação', where: 'Página Exportação' },
]

const CLICKS: ClickItem[] = [
	{
		name: 'Botões e links principais',
		where: 'Várias páginas: Onde encontrar, WhatsApp, downloads, banners e produtos',
		classes: ['ga-click'],
	},
	{ name: 'Menu do topo', where: 'Todas as páginas', classes: ['ga-menu-header'] },
	{ name: 'Links do rodapé', where: 'Todas as páginas', classes: ['ga-menu-footer'] },
	{ name: 'Comprar na tabela comparativa', where: 'Comparação de produtos', classes: ['ga-buy-product', 'ga-click'] },
	{ name: 'Comprar agora', where: 'Páginas da Garrafa Niva e do Everest Foz', classes: ['ga-buy-now'] },
	{ name: 'Escolher garrafa', where: 'Página da Garrafa Niva', classes: ['ga-choose-a-bottle'] },
	{ name: 'Vídeo da Garrafa Niva', where: 'Página da Garrafa Niva', classes: ['ga-watch-video'] },
	{ name: 'Comprar em revenda autorizada', where: 'Página do Inverter Plus', classes: ['ga-authorized'] },
	{ name: 'Conhecer produto', where: 'Página do Everest Foz', classes: ['ga-to-know'] },
	{ name: 'Comparar produtos', where: 'Páginas de produto', classes: ['ga-to-compare'] },
]

const PAGE_TRIGGER_TYPES = ['pageview', 'domReady', 'windowLoaded', 'init', 'consentInit']

@Component({
	selector: 'app-relatorio-gtm',
	templateUrl: './relatorio-gtm.component.html',
	styleUrls: ['./relatorio-gtm.component.scss'],
})
export class RelatorioGtmComponent implements OnInit {
	constructor(
		private title: Title,
		private meta: Meta,
		private router: Router,
		private auth: GtmReportAuthService,
		private gtmApi: GtmApiService
	) {}

	// Data da última revisão das listas FORMS e CLICKS no código do site
	reviewedAt = '23/09/2026'
	lang = 'pt'

	report: GtmReport | null = null
	loading = false
	error = ''

	tools: string[] = []
	forms: TrackedRow[] = []
	clicks: TrackedRow[] = []
	visits: PageVisits[] = []
	maxPlatformCount = 1

	tagRows: TagRow[] = []
	filteredTags: TagRow[] = []
	platformFilter = 'Todas'
	search = ''

	ngOnInit(): void {
		this.title.setTitle('Painel de medição - Everest')
		this.meta.updateTag({ name: 'robots', content: 'noindex, nofollow' }, "name='robots'")
		this.loadGtm()
	}

	async loadGtm(): Promise<void> {
		this.loading = true
		this.error = ''
		try {
			this.setReport(await this.gtmApi.getReport())
		} catch (error) {
			if (error instanceof GtmSessionExpired) {
				this.router.navigate(['/' + this.lang, 'relatorio-gtm-login'], {
					queryParams: { returnUrl: this.router.url },
				})
				return
			}
			this.error = (error as Error).message
		} finally {
			this.loading = false
		}
	}

	logout(): void {
		this.auth.logout()
		this.router.navigate(['/' + this.lang, 'relatorio-gtm-login'])
	}

	setPlatform(platform: string): void {
		this.platformFilter = platform
		this.filterTags()
	}

	filterTags(): void {
		const term = this.search.trim().toLowerCase()
		this.filteredTags = this.tagRows.filter(
			(row) =>
				(this.platformFilter === 'Todas' || row.tag.platform === this.platformFilter) &&
				(!term || `${row.name} ${row.tag.platform}`.toLowerCase().includes(term))
		)
	}

	private setReport(report: GtmReport): void {
		this.report = report
		const activeTags = report.tags.filter((t) => !t.paused)
		this.tools = unique(activeTags.map((t) => t.platform))
		this.maxPlatformCount = Math.max(1, ...report.platforms.map((p) => p.count))

		// Só entram na tela os formulários e cliques que chegam a alguma tag ativa
		this.forms = FORMS.map((form) =>
			this.trackedRow(form.name, form.where, form.event ? triggersForEvent(report.triggers, form.event) : [])
		).filter((row): row is TrackedRow => !!row)
		this.clicks = CLICKS.map((click) =>
			this.trackedRow(
				click.name,
				click.where,
				report.triggers.filter((t) => click.classes.some((c) => triggersForClickClass([t], c).length))
			)
		).filter((row): row is TrackedRow => !!row)
		this.visits = this.pageVisits(report)

		this.tagRows = report.tags
			.map((tag) => ({ tag, name: cleanName(tag.name), when: this.describeTag(tag, report) }))
			.sort((a, b) => a.tag.platform.localeCompare(b.tag.platform) || a.name.localeCompare(b.name))
		this.platformFilter = 'Todas'
		this.search = ''
		this.filterTags()
	}

	private trackedRow(name: string, where: string, triggers: GtmTrigger[]): TrackedRow | null {
		const active = ([] as GtmTag[]).concat(...triggers.map((t) => t.tags)).filter((t) => !t.paused)
		return active.length ? { name, where, tools: unique(active.map((t) => t.platform)) } : null
	}

	// Ferramentas que recebem as visitas: em todas as páginas ou só em páginas específicas
	private pageVisits(report: GtmReport): PageVisits[] {
		const byPage = new Map<string, string[]>()
		report.tags
			.filter((tag) => !tag.paused)
			.forEach((tag) =>
				tag.firingTriggerIds.forEach((id) => {
					const page = this.pageOfTrigger(id, report)
					if (page) {
						byPage.set(page, unique([...(byPage.get(page) || []), tag.platform]))
					}
				})
			)
		return [...byPage.entries()]
			.map(([page, tools]) => ({ page, tools }))
			.sort((a, b) => (a.page === 'Todas as páginas' ? -1 : b.page === 'Todas as páginas' ? 1 : 0))
	}

	private pageOfTrigger(id: string, report: GtmReport): string {
		if (BUILT_IN_TRIGGERS[id]) {
			return 'Todas as páginas'
		}
		const trigger = report.triggers.find((t) => t.id === id)
		if (!trigger || !PAGE_TRIGGER_TYPES.includes(trigger.type)) {
			return ''
		}
		const path = trigger.conditions.find((c) => /Page (Path|URL)/i.test(c.variable) && !c.negate)
		return path ? `Página ${pageName(path.value)}` : 'Todas as páginas'
	}

	// Quando a tag é acionada, em linguagem do dia a dia
	private describeTag(tag: GtmTag, report: GtmReport): string[] {
		const descriptions = tag.firingTriggerIds.map((id) => {
			if (BUILT_IN_TRIGGERS[id]) {
				return 'Todas as páginas'
			}
			const trigger = report.triggers.find((t) => t.id === id)
			return trigger ? this.describeTrigger(trigger) : ''
		})
		return unique(descriptions.filter((d) => d))
	}

	private describeTrigger(trigger: GtmTrigger): string {
		switch (trigger.type) {
			case 'pageview':
			case 'domReady':
			case 'windowLoaded':
			case 'init':
			case 'consentInit': {
				const path = trigger.conditions.find((c) => /Page (Path|URL)/i.test(c.variable) && !c.negate)
				return path ? `Página ${pageName(path.value)}` : 'Todas as páginas'
			}
			case 'customEvent': {
				const form = FORMS.find((f) => f.event && triggersForEvent([trigger], f.event).length)
				if (form) {
					return `Envio do formulário ${form.name}`
				}
				const event = expectedEventName(trigger)
				return event ? `Evento "${event}"` : 'Evento do site'
			}
			case 'click':
			case 'linkClick': {
				if (!trigger.conditions.length) {
					return 'Qualquer clique'
				}
				const click = CLICKS.find((c) => triggersForClickClass([trigger], c.classes[0]).length)
				if (click) {
					return `Clique em ${lowerFirst(click.name)}`
				}
				if (trigger.conditions.some((c) => /pdf|download/i.test(`${c.variable} ${c.value}`))) {
					return 'Download de arquivos PDF'
				}
				return 'Cliques específicos'
			}
			case 'scrollDepth':
				return 'Rolagem da página'
			case 'timer':
				return 'Tempo na página'
			case 'youTubeVideo':
				return 'Vídeos do YouTube'
			case 'formSubmission':
				return 'Envio de formulários'
			case 'elementVisibility':
				return 'Quando um item aparece na tela'
			default:
				return trigger.typeLabel
		}
	}
}

function unique(values: string[]): string[] {
	return [...new Set(values)]
}

function lowerFirst(text: string): string {
	return text.charAt(0).toLowerCase() + text.slice(1)
}

// "/pt/para-empresas" vira "Para empresas"
function pageName(path: string): string {
	const slug = path.replace(/^https?:\/\/[^/]+/, '').replace(/^\/(pt|es|en)(?=\/|$)/, '').replace(/^\/|\/$/g, '')
	if (!slug) {
		return 'Inicial'
	}
	const name = slug.split('/').pop()!.replace(/-/g, ' ')
	return name.charAt(0).toUpperCase() + name.slice(1)
}

// Tira emojis e símbolos que o time usa no começo do nome das tags no GTM
function cleanName(name: string): string {
	return name.replace(/^[^\p{L}\p{N}[(]+/u, '').trim() || name
}
